// Labels, drill-down links and connect actions for the Security Operations tiles (/soc).
// The backend returns stable metric keys only; every label shown on the page comes from here.
// `link(metrics, pending, access)` is the tile's "View details" target, only for a screen outside
// the SOC (Dark Web, AI Intelligence, Surface scan); a tile whose details are shown in the SOC itself
// has no link and so no button, never a jump to GRC. `access` is what the viewer may open (built by
// SocDashboard), read only by the Data tile.

const has = (metrics, key) => !!metrics && Object.prototype.hasOwnProperty.call(metrics, key);

const MICROSOFT_CONNECT = { path: "/settings/integrations", state: { expandSection: "microsoft" } };

// `issueKeys`: the metrics summed into the area's open-issue count, shown alike on the Overview chart,
// the tile headline and the menu badge; `issueLabel` names that count on the tile.
// A metric entry is [key, label] or [key, label, format(value, metrics)] for a value that needs its
// context. `notes`: [flagKey, text] shown when the summary sends that flag. A key the summary lists in
// the tile's `pending` is shown as waiting for the next sync, never as a number.
export const SOC_TILES = {
  incidents: {
    title: "Incidents & alerts",
    issueKeys: ["openIncidents", "openAlerts"],
    issueLabel: "open incidents & alerts",
    metrics: [
      ["openIncidents", "Open incidents"],
      ["openAlerts", "Open alerts"],
      ["critical", "Critical"],
      ["high", "High"],
      ["medium", "Medium"],
      ["low", "Low"],
    ],
    caption: "Defender XDR",
    connect: MICROSOFT_CONNECT,
  },
  identity: {
    title: "Identity",
    issueKeys: ["nhiFailing", "identityThreats"],
    issueLabel: "open identity issues",
    metrics: [
      ["identities", "Identities"],
      ["privileged", "Privileged accounts"],
      ["nonHuman", "Non-human identities"],
      ["guests", "Guest accounts"],
      ["mfaPercent", "MFA adoption"],
      ["nhiFailing", "Failing non-human identity checks"],
      ["identityThreats", "Identity threats"],
    ],
    connect: MICROSOFT_CONNECT,
  },
  endpoint: {
    title: "Endpoint",
    issueKeys: ["highRiskDevices", "openCves", "openRecommendations"],
    issueLabel: "open endpoint issues",
    metrics: [
      ["devices", "Devices"],
      ["highRiskDevices", "High-risk devices"],
      ["inactiveDevices", "Inactive devices"],
      // Unique CVEs; one CVE on many devices is one CVE and many instances (see SocTopVulnerabilities).
      ["openCves", "Open CVEs"],
      ["criticalCves", "Critical CVEs"],
      ["cveInstances", "CVE instances on devices"],
      ["exposedDevices", "Devices with critical/high CVEs"],
      ["openRecommendations", "Open recommendations"],
    ],
    connect: MICROSOFT_CONNECT,
  },
  cloud: {
    title: "Cloud",
    issueKeys: ["defenderFailing", "awsFailing", "gcpFailing", "wizFailing", "attackPaths"],
    issueLabel: "open cloud issues",
    metrics: [
      ["defenderFailing", "Failing Defender for Cloud checks"],
      ["awsFailing", "Failing AWS checks"],
      ["gcpFailing", "Failing GCP checks"],
      ["wizFailing", "Failing Wiz checks"],
      ["attackPaths", "Attack paths"],
    ],
    connect: MICROSOFT_CONNECT,
  },
  cloudApps: {
    title: "Cloud apps",
    issueKeys: ["activeAlerts", "unsanctionedHighRiskApps"],
    issueLabel: "open cloud app issues",
    metrics: [
      ["activeAlerts", "Active alerts"],
      ["highSeverityAlerts", "High-severity alerts"],
      ["dataTheftAlerts", "Data-theft alerts"],
      ["unsanctionedAppAlerts", "Unsanctioned-app alerts"],
      ["unsanctionedApps", "Unsanctioned apps"],
      ["highRiskApps", "High-risk apps (risk score 0-3)"],
      ["unsanctionedHighRiskApps", "Unsanctioned high-risk apps"],
    ],
    notes: [
      ["alertsReadCapped", "Alert counts are a minimum: the alert read was capped."],
      ["appsReadCapped", "App counts cover a capped sample of discovered apps."],
    ],
    // Alerts inside the Defender XDR incidents Axaris holds are counted under Incidents & alerts.
    caption: "Alerts outside tracked Defender XDR incidents",
    connect: MICROSOFT_CONNECT,
  },
  siem: {
    title: "SIEM",
    issueKeys: ["sentinelOpenIncidents", "splunkFailing"],
    issueLabel: "open SIEM issues",
    metrics: [
      ["sentinelOpenIncidents", "Sentinel open incidents"],
      ["sentinelHighIncidents", "Sentinel high-severity incidents"],
      ["splunkFailing", "Failing Splunk health checks"],
    ],
    connect: MICROSOFT_CONNECT,
  },
  detectionRules: {
    title: "Detection rules",
    issueKeys: ["rulesBelowBaseline"],
    issueLabel: "SIEMs below the Axaris baseline",
    metrics: [
      ["sentinelActiveRules", "Sentinel active rules"],
      ["splunkAlertRules", "Splunk alert rules"],
      ["rulesBelowBaseline", "SIEMs below the Axaris baseline at last check"],
    ],
    caption: "Axaris baseline: Sentinel 10 rules, Splunk 5. A count shows rules are configured, not that detection works.",
    connect: MICROSOFT_CONNECT,
  },
  network: {
    title: "Network",
    issueKeys: ["openSignals"],
    issueLabel: "open firewall signals",
    metrics: [
      ["openSignals", "Open firewall signals"],
      ["critical", "Critical"],
      ["high", "High"],
      ["vpnFailedLogins", "VPN failed-login alerts"],
    ],
    caption: "Fortinet",
    // The tile counts signals open now, however old; the trend chart counts those opened and resolved
    // in its range, so it can be empty while signals are open.
    footnote: "Open: signals not yet resolved, however long ago they were raised. The trend chart counts signals opened and resolved in its range, so it can be empty while older signals stay open.",
    connect: { path: "/settings/integrations", state: { expandSection: "fortinet" } },
  },
  external: {
    title: "External threat",
    issueKeys: ["openEsmRisks", "openSecretsRisks", "openLeakedCredentials", "openRansomwareRisks"],
    issueLabel: "open external risks",
    metrics: [
      ["esmEvents", "Attack-surface findings"],
      ["darkWebEvents", "Dark web exposures"],
      ["secretsEvents", "Exposed secrets"],
      ["brandEvents", "Brand threats"],
      ["ransomwareEvents", "Ransomware activity"],
      ["openEsmRisks", "Attack-surface risks"],
      ["openSecretsRisks", "Exposed-secret risks"],
      ["openLeakedCredentials", "Leaked-credential risks"],
      ["openRansomwareRisks", "Ransomware risks"],
      ["openBrandThreats", "Open brand threats"],
    ],
    // One row per source with its two figures side by side, so "0 new, 48 open" reads as one fact (the
    // findings are old and still unresolved), not as two figures that disagree. [open key, new key]; a
    // null key is a measure the source doesn't have. Brand threats are counted from their own status, not as risks.
    matrix: {
      columns: ["Open now", "New in 30 days"],
      rows: [
        { label: "Attack surface", source: "Surface scan", keys: ["openEsmRisks", "esmEvents"] },
        { label: "Exposed secrets", source: "Secrets scan", keys: ["openSecretsRisks", "secretsEvents"] },
        { label: "Dark web", source: "Dark web monitoring", keys: ["openLeakedCredentials", "darkWebEvents"] },
        { label: "Brand threats", source: "Brand protection", keys: ["openBrandThreats", "brandEvents"] },
        { label: "Ransomware", source: "Ransomware monitoring", keys: ["openRansomwareRisks", "ransomwareEvents"] },
      ],
      note: "Open now: risks and brand threats not yet resolved, however long ago they were found. New in 30 days: "
        + "findings first detected in the last 30 days. A source can have 0 new and still have open ones. Brand threats "
        + "are worked in Brand protection, so they aren't in the risk total or the incident queue.",
    },
    clickable: ["esmEvents", "darkWebEvents", "secretsEvents", "brandEvents", "ransomwareEvents",
      "openEsmRisks", "openSecretsRisks", "openLeakedCredentials", "openRansomwareRisks", "openBrandThreats"],
    // The Dark Web module landing page: a starting point, not a one-to-one view of every count.
    link: () => "/DarkWebDashboard",
    connect: { path: "/ConnectorsConfig?tab=surface", state: null },
  },
  data: {
    title: "Data",
    issueKeys: ["aiDataLeaks"],
    issueLabel: "AI app data-leak alerts",
    metrics: [["aiDataLeaks", "AI app data-leak alerts"]],
    caption: "Covers AI apps only",
    // The estate-wide AI Data Leak Findings list, only when the viewer can open it: the AI module is
    // on (otherwise the page redirects to the dashboard) and they are an org-wide viewer.
    link: (metrics, pending, access) => (access?.canOpenAiDataLeaks ? "/AIIntelligence?tab=leaks" : null),
    connect: MICROSOFT_CONNECT,
  },
  email: {
    title: "Email",
    issueKeys: ["emailSettingsFailing", "domainsMissingEmailAuth"],
    issueLabel: "open email issues",
    metrics: [
      [
        "emailSettingsFailing",
        "Failing Exchange Online & Defender for Office 365 settings",
        (v, m) => (has(m, "emailSettings") ? `${v} of ${m.emailSettings}` : String(v)),
      ],
      ["domainsMissingEmailAuth", "Domains missing DMARC, SPF or DKIM"],
      ["missingDmarc", "Missing DMARC"],
      ["missingSpf", "Missing SPF"],
      ["missingDkim", "Missing DKIM"],
      [
        "phishPronePercent",
        "Phishing click rate (KnowBe4)",
        (v, m) => `${v}% · average of the last ${m.phishTests} ${m.phishTests === 1 ? "test" : "tests"}`,
      ],
    ],
    // Configuration and DNS records; email alerts are not attributed here yet.
    caption: "Settings and DNS records, not alerts",
    // DNS records alone are worked in Surface scan; settings and KnowBe4 checks are shown in the SOC.
    link: (metrics, pending = []) => (has(metrics, "domainsMissingEmailAuth") && !has(metrics, "emailSettingsFailing")
      && !has(metrics, "phishPronePercent") && !pending.includes("phishPronePercent") ? "/DarkWebEsmDashboard" : null),
    connect: MICROSOFT_CONNECT,
  },
  agenticAi: {
    title: "Agentic AI",
    // Open AI posture risks are the area's issues; the agent counts describe the estate.
    issueKeys: ["openAiRisks"],
    issueLabel: "open AI risks",
    metrics: [
      ["agents", "AI agents"],
      ["autonomousAgents", "Acting with no human in the loop"],
      ["shadowAgents", "Shadow agents (not in any registry)"],
      ["ownerlessAgents", "Agents with no owner"],
      ["overPrivilegedAgents", "Over-privileged or risky agents"],
      ["containedAgents", "Contained agents"],
      ["openAiRisks", "Open AI posture risks"],
      ["criticalAiRisks", "Critical/high AI risks"],
    ],
    caption: "AI Intelligence discovery",
    link: () => "/AIIntelligence",
    connect: MICROSOFT_CONNECT,
  },
};
